import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { C } from '../constants';

// Sample deck types that will flow into the engine
const DECK_TYPES = [
  { id: 1, name: 'Pitch Deck', color: '#3B82F6', icon: '📊' },
  { id: 2, name: 'Sales Deck', color: '#8B5CF6', icon: '💼' },
  { id: 3, name: 'Investor Deck', color: '#EC4899', icon: '📈' },
  { id: 4, name: 'Product Deck', color: '#F59E0B', icon: '🚀' },
  { id: 5, name: 'Marketing Deck', color: '#10B981', icon: '📣' },
];

// 5 dimensions of scoring
const DIMENSIONS = [
  { name: 'CREDIBILITY', question: 'Do you have proof?', color: '#3B82F6' },
  { name: 'CLARITY', question: 'Is the story easy to follow?', color: '#8B5CF6' },
  { name: 'MARKET FIT', question: 'Is the opportunity compelling?', color: '#EC4899' },
  { name: 'COMPETITIVE EDGE', question: 'Why you vs. alternatives?', color: '#F59E0B' },
  { name: 'PRESENTATION', question: 'Does it hook in 3 slides?', color: '#10B981' },
];

// Score outputs
const SCORE_OUTPUTS = [
  { score: '70+', label: 'Decision-ready. Go get that yes.', color: C.green, bg: 'rgba(16, 185, 129, 0.15)' },
  { score: '50-69', label: 'Close, but not closing.', color: C.yellow, bg: 'rgba(245, 158, 11, 0.15)' },
  { score: '<50', label: 'Needs work.', color: C.red, bg: 'rgba(239, 68, 68, 0.15)' },
];

// Mobile AI Engine - compact version
const MobileAIEngine = ({ isProcessing, currentDimension, theme }) => (
  <motion.div
    style={{ position: 'relative', width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    animate={{ scale: isProcessing ? [1, 1.03, 1] : 1 }}
    transition={{ duration: 0.5, repeat: isProcessing ? Infinity : 0 }}
  >
    {/* Outer ring */}
    <motion.div
      style={{
        position: 'absolute',
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        border: `2px solid ${C.secondary}30`,
        background: `radial-gradient(circle, ${C.secondary}08 0%, transparent 70%)`,
      }}
      animate={{ rotate: isProcessing ? 360 : 0 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
    />
    {/* Dimension dots */}
    {DIMENSIONS.map((dim, i) => {
      const angle = (i * 72 - 90) * (Math.PI / 180);
      const radius = 50;
      return (
        <motion.div
          key={dim.name}
          style={{
            position: 'absolute',
            left: `calc(50% + ${Math.cos(angle) * radius}px - 5px)`,
            top: `calc(50% + ${Math.sin(angle) * radius}px - 5px)`,
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: currentDimension === i ? dim.color : `${dim.color}40`,
            boxShadow: currentDimension === i ? `0 0 12px ${dim.color}` : 'none',
          }}
          animate={{ scale: currentDimension === i ? 1.3 : 1 }}
        />
      );
    })}
    {/* Center core */}
    <motion.div
      style={{
        width: '70px',
        height: '70px',
        background: theme === 'dark' ? `linear-gradient(135deg, ${C.secondary}30, ${C.secondary}10)` : `linear-gradient(135deg, ${C.secondary}20, ${C.secondary}05)`,
        borderRadius: '16px',
        border: `2px solid ${C.secondary}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'rotate(45deg)',
        boxShadow: `0 0 30px ${C.secondary}30`,
      }}
    >
      <div style={{ transform: 'rotate(-45deg)', textAlign: 'center' }}>
        <span style={{ fontSize: '9px', fontWeight: 700, color: C.secondary, letterSpacing: '0.08em' }}>DecX AI</span>
        <div style={{ fontSize: '6px', opacity: 0.7, marginTop: '2px', color: theme === 'dark' ? C.white : C.black }}>ENGINE</div>
      </div>
    </motion.div>
  </motion.div>
);

// Mobile Score Output
const MobileScoreOutput = ({ score, isVisible, delay }) => (
  <motion.div
    initial={{ y: 10, opacity: 0 }}
    animate={isVisible ? { y: 0, opacity: 1 } : { y: 10, opacity: 0 }}
    transition={{ duration: 0.4, delay }}
    style={{
      padding: '10px 14px',
      background: score.bg,
      border: `1px solid ${score.color}40`,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    <div style={{ fontSize: '18px', fontWeight: 700, color: score.color, minWidth: '50px', fontFamily: "'Sansation', sans-serif" }}>
      {score.score}
    </div>
    <div style={{ fontSize: '11px', lineHeight: 1.3, opacity: 0.9 }}>{score.label}</div>
  </motion.div>
);

// Floating Deck Slide Component
const FloatingDeck = ({ deck, index, onComplete, isActive }) => {
  const startX = -200 - (index * 80);
  const startY = Math.sin(index * 0.8) * 60;
  
  return (
    <motion.div
      initial={{ x: startX, y: startY, opacity: 0, scale: 0.6, rotate: -15 }}
      animate={isActive ? {
        x: [-200, 0],
        y: [startY, 0],
        opacity: [0, 1, 1, 0],
        scale: [0.6, 1, 0.8, 0.3],
        rotate: [-15, 0, 5, 0],
      } : { x: startX, y: startY, opacity: 0 }}
      transition={{
        duration: 2.5,
        ease: "easeInOut",
        times: [0, 0.4, 0.8, 1],
      }}
      onAnimationComplete={onComplete}
      style={{
        position: 'absolute',
        left: '15%',
        top: '50%',
        zIndex: 10,
      }}
    >
      <div
        style={{
          width: '80px',
          height: '100px',
          background: `linear-gradient(135deg, ${deck.color}, ${deck.color}80)`,
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 10px 40px ${deck.color}40, 0 0 20px ${deck.color}30`,
          border: '2px solid rgba(255,255,255,0.2)',
        }}
      >
        <span style={{ fontSize: '28px', marginBottom: '4px' }}>{deck.icon}</span>
        <span style={{ 
          fontSize: '9px', 
          fontWeight: 700, 
          color: 'white',
          textAlign: 'center',
          padding: '0 4px',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}>
          {deck.name}
        </span>
      </div>
    </motion.div>
  );
};

// Central AI Engine Component
const AIEngine = ({ isProcessing, currentDimension }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      style={{
        position: 'relative',
        width: '200px',
        height: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      animate={{
        scale: isProcessing ? [1, 1.05, 1] : 1,
      }}
      transition={{
        duration: 0.5,
        repeat: isProcessing ? Infinity : 0,
      }}
    >
      {/* Outer glow rings */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: `${180 + i * 30}px`,
            height: `${180 + i * 30}px`,
            borderRadius: '50%',
            border: `2px solid ${C.secondary}${40 - i * 10}`,
            background: `radial-gradient(circle, ${C.secondary}${10 - i * 3} 0%, transparent 70%)`,
          }}
          animate={{
            rotate: isProcessing ? [0, 360] : 0,
            scale: isProcessing ? [1, 1.1, 1] : 1,
          }}
          transition={{
            rotate: { duration: 8 + i * 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity, delay: i * 0.3 },
          }}
        />
      ))}
      
      {/* Dimension indicators around the engine */}
      {DIMENSIONS.map((dim, i) => {
        const angle = (i * 72 - 90) * (Math.PI / 180);
        const radius = 100;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        const isActive = currentDimension === i;
        
        return (
          <motion.div
            key={dim.name}
            style={{
              position: 'absolute',
              left: `calc(50% + ${x}px - 8px)`,
              top: `calc(50% + ${y}px - 8px)`,
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: isActive ? dim.color : `${dim.color}40`,
              boxShadow: isActive ? `0 0 20px ${dim.color}, 0 0 40px ${dim.color}50` : 'none',
            }}
            animate={{
              scale: isActive ? [1, 1.5, 1] : 1,
            }}
            transition={{ duration: 0.3 }}
          />
        );
      })}
      
      {/* Central hexagon core */}
      <motion.div
        style={{
          width: '120px',
          height: '120px',
          background: theme === 'dark' 
            ? `linear-gradient(135deg, ${C.secondary}30, ${C.secondary}10)`
            : `linear-gradient(135deg, ${C.secondary}20, ${C.secondary}05)`,
          borderRadius: '24px',
          border: `3px solid ${C.secondary}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 60px ${C.secondary}40, inset 0 0 30px ${C.secondary}20`,
          transform: 'rotate(45deg)',
          overflow: 'hidden',
        }}
        animate={{
          boxShadow: isProcessing 
            ? [`0 0 60px ${C.secondary}40`, `0 0 80px ${C.secondary}60`, `0 0 60px ${C.secondary}40`]
            : `0 0 60px ${C.secondary}40`,
        }}
        transition={{ duration: 1, repeat: isProcessing ? Infinity : 0 }}
      >
        <div style={{ transform: 'rotate(-45deg)', textAlign: 'center' }}>
          <motion.div
            animate={{
              opacity: isProcessing ? [0.5, 1, 0.5] : 1,
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span style={{ fontSize: '11px', fontWeight: 700, color: C.secondary, letterSpacing: '0.1em' }}>
              DecX AI
            </span>
            <div style={{ 
              fontSize: '8px', 
              opacity: 0.7, 
              marginTop: '4px',
              color: theme === 'dark' ? C.white : C.black 
            }}>
              INTELLIGENT ENGINE
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Processing indicator */}
      {isProcessing && currentDimension !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'absolute',
            bottom: '-30px',
            fontSize: '11px',
            fontWeight: 600,
            color: DIMENSIONS[currentDimension]?.color || C.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          Analyzing: {DIMENSIONS[currentDimension]?.name}
        </motion.div>
      )}
    </motion.div>
  );
};

// Score Output Component
const ScoreOutput = ({ score, isVisible, delay }) => {
  return (
    <motion.div
      initial={{ x: 50, opacity: 0, scale: 0.8 }}
      animate={isVisible ? { x: 0, opacity: 1, scale: 1 } : { x: 50, opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.5, delay }}
      style={{
        padding: '16px 20px',
        background: score.bg,
        border: `2px solid ${score.color}50`,
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px',
        boxShadow: isVisible ? `0 4px 20px ${score.color}20` : 'none',
      }}
    >
      <div style={{ 
        fontSize: '24px', 
        fontWeight: 700, 
        color: score.color, 
        minWidth: '60px',
        fontFamily: "'Sansation', sans-serif"
      }}>
        {score.score}
      </div>
      <div style={{ fontSize: '13px', lineHeight: 1.4, opacity: 0.9 }}>
        {score.label}
      </div>
    </motion.div>
  );
};

// Main Component
export const DecXEngineAnimation = () => {
  const { theme } = useTheme();
  const [activeDeck, setActiveDeck] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentDimension, setCurrentDimension] = useState(null);
  const [showScores, setShowScores] = useState(false);
  const [cycleComplete, setCycleComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animation cycle
  useEffect(() => {
    const runCycle = async () => {
      // Reset
      setShowScores(false);
      setCycleComplete(false);
      setCurrentDimension(null);
      
      // Wait a bit before starting
      await new Promise(r => setTimeout(r, 500));
      
      // Process each deck
      for (let i = 0; i < DECK_TYPES.length; i++) {
        setActiveDeck(i);
        setIsProcessing(true);
        
        // Cycle through dimensions while processing
        for (let d = 0; d < DIMENSIONS.length; d++) {
          setCurrentDimension(d);
          await new Promise(r => setTimeout(r, isMobile ? 300 : 400));
        }
        
        await new Promise(r => setTimeout(r, 300));
      }
      
      setIsProcessing(false);
      setCurrentDimension(null);
      
      // Show scores
      await new Promise(r => setTimeout(r, 300));
      setShowScores(true);
      setCycleComplete(true);
      
      // Wait before restarting
      await new Promise(r => setTimeout(r, 4000));
    };

    runCycle();
    const interval = setInterval(runCycle, isMobile ? 14000 : 18000);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Mobile Layout - show all elements including floating decks
  if (isMobile) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          padding: '16px 8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          overflow: 'visible',
        }}
        data-testid="decx-engine-animation"
      >
        {/* Incoming decks row */}
        <div style={{ width: '100%', marginBottom: '8px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.secondary, letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'center' }}>
            INCOMING DECKS
          </div>
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {DECK_TYPES.map((deck, i) => (
              <motion.div
                key={deck.id}
                animate={{
                  scale: activeDeck === i && isProcessing ? [1, 1.1, 1] : 1,
                  opacity: activeDeck === i && isProcessing ? 1 : 0.5,
                  borderColor: activeDeck === i && isProcessing ? deck.color : 'transparent',
                }}
                transition={{ duration: 0.3 }}
                style={{
                  padding: '6px 10px',
                  background: `${deck.color}20`,
                  border: `2px solid transparent`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                }}
              >
                <span>{deck.icon}</span>
                <span style={{ color: deck.color }}>{deck.name.split(' ')[0]}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Arrow down into engine */}
        <motion.div
          animate={{ y: isProcessing ? [0, 4, 0] : 0, opacity: isProcessing ? 1 : 0.4 }}
          transition={{ duration: 0.8, repeat: isProcessing ? Infinity : 0 }}
        >
          <svg width="20" height="24" viewBox="0 0 20 24">
            <path d="M10 0 L10 18 M3 12 L10 20 L17 12" fill="none" stroke={C.secondary} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </motion.div>

        {/* Central Engine */}
        <div style={{ position: 'relative' }}>
          <MobileAIEngine isProcessing={isProcessing} currentDimension={currentDimension} theme={theme} />
        </div>

        {/* Dimensions being analyzed */}
        <div style={{ width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.secondary, letterSpacing: '0.08em', marginBottom: '8px' }}>
            5 DIMENSIONS ANALYZED
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
            {DIMENSIONS.map((dim, i) => (
              <motion.div
                key={dim.name}
                animate={{ 
                  scale: currentDimension === i ? 1.05 : 1, 
                  opacity: currentDimension === i ? 1 : 0.5,
                  background: currentDimension === i ? `${dim.color}30` : 'transparent',
                }}
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${currentDimension === i ? dim.color : 'transparent'}`,
                  borderRadius: '100px',
                  fontSize: '9px',
                  fontWeight: 600,
                  color: currentDimension === i ? dim.color : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                }}
              >
                {dim.name}
              </motion.div>
            ))}
          </div>
          {isProcessing && currentDimension !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                marginTop: '8px',
                fontSize: '11px',
                fontStyle: 'italic',
                color: DIMENSIONS[currentDimension]?.color || C.secondary,
              }}
            >
              "{DIMENSIONS[currentDimension]?.question}"
            </motion.div>
          )}
        </div>

        {/* Arrow down to scores */}
        <motion.div
          animate={{ y: showScores ? [0, 4, 0] : 0, opacity: showScores ? 1 : 0.4 }}
          transition={{ duration: 0.8, repeat: showScores ? Infinity : 0 }}
        >
          <svg width="20" height="24" viewBox="0 0 20 24">
            <path d="M10 0 L10 18 M3 12 L10 20 L17 12" fill="none" stroke={C.secondary} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </motion.div>

        {/* Score outputs */}
        <div style={{ width: '100%' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.secondary, letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'center' }}>
            SCORE OUTPUT
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {SCORE_OUTPUTS.map((score, i) => (
              <MobileScoreOutput key={score.score} score={score} isVisible={showScores} delay={i * 0.1} />
            ))}
          </div>
          {cycleComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                marginTop: '10px',
                padding: '8px 12px',
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: '8px',
                fontSize: '11px',
                color: C.green,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <span>✓</span> Analysis Complete
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        overflow: 'hidden',
      }}
      data-testid="decx-engine-animation"
    >
      {/* Background grid effect */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: theme === 'dark'
            ? `radial-gradient(circle at 50% 50%, ${C.secondary}08 0%, transparent 50%)`
            : `radial-gradient(circle at 50% 50%, ${C.secondary}05 0%, transparent 50%)`,
          pointerEvents: 'none',
        }}
      />
      
      {/* Main content container */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '40px',
          maxWidth: '1000px',
          width: '100%',
          position: 'relative',
        }}
      >
        {/* Left side - Incoming decks with dimensions label */}
        <div style={{ 
          flex: '1', 
          position: 'relative', 
          height: '300px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              color: C.secondary,
              letterSpacing: '0.12em',
              marginBottom: '8px'
            }}>
              5 Dimensions We Score
            </h4>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '6px',
              alignItems: 'flex-start',
            }}>
              {DIMENSIONS.map((dim, i) => (
                <motion.div
                  key={dim.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: currentDimension === i ? 1 : 0.5,
                  }}
                  animate={{
                    x: currentDimension === i ? 5 : 0,
                    scale: currentDimension === i ? 1.05 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: dim.color,
                      boxShadow: currentDimension === i ? `0 0 10px ${dim.color}` : 'none',
                    }}
                  />
                  <span style={{ 
                    fontSize: '11px', 
                    fontWeight: 600,
                    fontStyle: 'italic',
                    color: theme === 'dark' ? C.white : C.black,
                  }}>
                    {dim.question}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Animated decks */}
          <div style={{ position: 'relative', width: '100%', height: '150px' }}>
            {DECK_TYPES.map((deck, i) => (
              <FloatingDeck
                key={deck.id}
                deck={deck}
                index={i}
                isActive={activeDeck === i && isProcessing}
                onComplete={() => {}}
              />
            ))}
            
            {/* Flow arrow */}
            <motion.div
              style={{
                position: 'absolute',
                left: '60%',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
              }}
              animate={{
                x: [0, 10, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <svg width="60" height="24" viewBox="0 0 60 24">
                <defs>
                  <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={`${C.secondary}40`} />
                    <stop offset="100%" stopColor={C.secondary} />
                  </linearGradient>
                </defs>
                <path
                  d="M0 12 L45 12 M35 4 L48 12 L35 20"
                  fill="none"
                  stroke="url(#arrowGradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>
          </div>
        </div>
        
        {/* Center - AI Engine */}
        <div style={{ 
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <AIEngine isProcessing={isProcessing} currentDimension={currentDimension} />
        </div>
        
        {/* Right side - Score outputs */}
        <div style={{ 
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
        }}>
          {/* Flow arrow */}
          <motion.div
            style={{
              position: 'absolute',
              left: '-30px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            animate={{
              x: showScores ? [0, 10, 0] : 0,
              opacity: showScores ? 1 : 0.3,
            }}
            transition={{
              x: { duration: 1.5, repeat: showScores ? Infinity : 0, ease: "easeInOut" },
              opacity: { duration: 0.3 },
            }}
          >
            <svg width="40" height="24" viewBox="0 0 40 24">
              <defs>
                <linearGradient id="arrowGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={C.secondary} />
                  <stop offset="100%" stopColor={`${C.secondary}40`} />
                </linearGradient>
              </defs>
              <path
                d="M0 12 L30 12 M20 4 L33 12 L20 20"
                fill="none"
                stroke="url(#arrowGradient2)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ marginBottom: '16px', marginLeft: '20px' }}
          >
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              color: C.secondary,
              letterSpacing: '0.12em',
            }}>
              What Your Score Means
            </h4>
          </motion.div>
          
          <div style={{ marginLeft: '20px' }}>
            {SCORE_OUTPUTS.map((score, i) => (
              <ScoreOutput 
                key={score.score} 
                score={score} 
                isVisible={showScores}
                delay={i * 0.15}
              />
            ))}
          </div>
          
          {/* Processing status */}
          <AnimatePresence>
            {cycleComplete && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{
                  marginTop: '16px',
                  marginLeft: '20px',
                  padding: '12px 20px',
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: C.green,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  ✓
                </motion.span>
                Analysis Complete
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default DecXEngineAnimation;
